import type { LucideIcon } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { Banknote, Building2, CreditCard, History, KeyRound, Landmark, MapPin, SlidersHorizontal, User, Users } from 'lucide-react';
import { can, type MemberRole, type Permission } from '@repo/shared-types';

/** One source for the `/settings/:section` sub-nav (desktop) and drill-in list
 *  (phone) — grouped by who owns the setting, not by screen history. */
export type SettingsSection =
  | 'organization'
  | 'members'
  | 'branches'
  | 'bank'
  | 'billing'
  | 'billing-rules'
  | 'display'
  | 'audit'
  | 'profile'
  | 'password';

export type SettingsItemDef = {
  key: SettingsSection;
  icon: LucideIcon;
  label: { ns: 'settings'; key: string };
};

export type SettingsGroupDef = {
  key: 'org' | 'account';
  label: { ns: 'settings'; key: string };
  perm?: Permission;
  items: SettingsItemDef[];
};

export const SETTINGS_GROUPS: SettingsGroupDef[] = [
  {
    key: 'org',
    label: { ns: 'settings', key: 'groups.org' },
    perm: 'manage:settings',
    items: [
      { key: 'organization', icon: Building2, label: { ns: 'settings', key: 'sections.organization.title' } },
      { key: 'members', icon: Users, label: { ns: 'settings', key: 'sections.members.title' } },
      { key: 'branches', icon: MapPin, label: { ns: 'settings', key: 'sections.branches.title' } },
      { key: 'bank', icon: Landmark, label: { ns: 'settings', key: 'sections.bank.title' } },
      { key: 'billing', icon: CreditCard, label: { ns: 'settings', key: 'sections.billing.title' } },
      { key: 'billing-rules', icon: Banknote, label: { ns: 'settings', key: 'sections.billing-rules.title' } },
      { key: 'display', icon: SlidersHorizontal, label: { ns: 'settings', key: 'sections.display.title' } },
      { key: 'audit', icon: History, label: { ns: 'settings', key: 'sections.audit.title' } },
    ],
  },
  {
    key: 'account',
    label: { ns: 'settings', key: 'groups.account' },
    items: [
      { key: 'profile', icon: User, label: { ns: 'settings', key: 'sections.profile.title' } },
      { key: 'password', icon: KeyRound, label: { ns: 'settings', key: 'sections.password.title' } },
    ],
  },
];

/** MEMBER (assistant teacher) only manages their own account — drop the `org`
 *  group when the role lacks its perm. */
export function settingsSectionsForRole(role: MemberRole | null): SettingsGroupDef[] {
  return SETTINGS_GROUPS.filter((g) => !g.perm || (role !== null && can(role, g.perm)));
}

/** Legacy `?tab=` query param → new section key. Unknown/missing → null (no
 *  redirect target; caller falls through to the role default). */
export function legacySettingsRedirect(search: string): SettingsSection | null {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  switch (tab) {
    case 'profile':
      return 'organization';
    case 'team':
      return 'members';
    case 'bank':
      return 'bank';
    case 'preferences':
      return 'billing-rules';
    default:
      return null;
  }
}

export function defaultSectionForRole(role: MemberRole | null): SettingsSection {
  return role !== null && can(role, 'manage:settings') ? 'organization' : 'profile';
}

export function isSettingsSection(section: string, role: MemberRole | null): boolean {
  return settingsSectionsForRole(role).some((g) => g.items.some((i) => i.key === section));
}

/**
 * Above this width settings keeps its vertical rail; at or below it, settings
 * uses the narrow shell (`/settings` as a parent list, each section a child
 * page with a `← Cài đặt` back row).
 *
 * 1023, not the app-wide 768: the rail is a THIRD column competing with the app
 * sidebar, and the old threshold keyed off window width rather than off the
 * width left for content. At a 768px window the rail left the content column
 * 256px (sidebar 256 + padding 51.2 + rail 179.2 + gap 25.6 — `w-56` renders
 * 179.2px, not 224, because --spacing is 0.2rem). At 1024 the narrow shell
 * gives 672px, which is where `max-w-2xl` caps it anyway.
 *
 * DELIBERATELY LOCAL. `useIsMobile` keeps its own 768px breakpoint because it
 * drives the app's bottom tab bar — widening that would put a phone tab bar on
 * a tablet.
 */
export const SETTINGS_NARROW_MAX_PX = 1023;

/** `useIsMobile()` is false on its first render (state set in an effect), so a
 *  route decision made on that render alone goes the desktop way on phones.
 *  Combine it with a synchronous media-query read for first-paint decisions.
 *
 *  Named "narrow", not "mobile": below 1024 this is a tablet, not a phone.
 *
 *  PURE, NOT REACTIVE: this is a one-shot read with no listener of its own —
 *  fine as a first-paint fallback, wrong as the sole input to a render
 *  decision. `useIsMobile()`'s own listener only fires at its 768px
 *  boundary, so a resize that stays entirely inside 768–1023px (e.g.
 *  1200→900, or 1000→1030) crosses SETTINGS_NARROW_MAX_PX without ever
 *  crossing 768 and nothing re-renders. Components must use
 *  `useIsSettingsNarrow` below, which subscribes to this threshold's own
 *  media query. Kept exported and pinned by tests below because it is the
 *  obvious "fix" to reach for next year, and it isn't one. */
export function isNarrowNow(isMobileFromHook: boolean): boolean {
  return (
    isMobileFromHook ||
    (typeof window !== 'undefined' && window.matchMedia(`(max-width: ${SETTINGS_NARROW_MAX_PX}px)`).matches)
  );
}

const SETTINGS_NARROW_QUERY = `(max-width: ${SETTINGS_NARROW_MAX_PX}px)`;

/** `useSyncExternalStore` subscribe for `useIsSettingsNarrow`. Exported (not
 *  just used inline) so settingsNav.test.ts can pin the exact wiring — query
 *  string, event name, and that the cleanup removes the same listener —
 *  without rendering a component; this workspace's `bun test` has no DOM. */
export function subscribeSettingsNarrowQuery(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const mql = window.matchMedia(SETTINGS_NARROW_QUERY);
  mql.addEventListener('change', onStoreChange);
  return () => mql.removeEventListener('change', onStoreChange);
}

/** `useSyncExternalStore` snapshot for `useIsSettingsNarrow`: the settings
 *  threshold's own current match state (independent of `useIsMobile`'s). */
export function readSettingsNarrowQuery(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(SETTINGS_NARROW_QUERY).matches;
}

/**
 * Reactive replacement for calling `isNarrowNow` directly at render time.
 * Subscribes to `SETTINGS_NARROW_MAX_PX`'s own `(max-width: 1023px)` query
 * with `useSyncExternalStore` — same pattern as `useIsMobile` in
 * `packages/ui/src/hooks/use-mobile.tsx` — so a live resize that crosses
 * *either* the 768px `useIsMobile` boundary or the local 1023px boundary
 * re-renders the settings shell. Deliberately local to settings; does not
 * touch `useIsMobile`/`MOBILE_BREAKPOINT`.
 */
export function useIsSettingsNarrow(isMobileFromHook: boolean): boolean {
  const mqNarrow = useSyncExternalStore(subscribeSettingsNarrowQuery, readSettingsNarrowQuery, () => false);
  return isMobileFromHook || mqNarrow;
}
